import { readConsent } from './cookieConsent';

// First-touch referral (HS-519): the first valid ?ref= is kept for 30 days
// and carried into the panel links
export const REFERRAL_STORAGE_KEY = 'hs-referral';
export const REFERRAL_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;
const REFERRAL_CODE_PATTERN = /^[A-Z2-9]{8}$/;
const PANEL_HOSTS = ['admin.hostsoft.io', 'dev-be.nooxi.app'];
const API_URL = process.env.GATSBY_HS_API_URL || 'https://api.nooxi.app';

// Visit of this page session; its id is only persisted with marketing consent
let currentVisit = null;

const readStorage = () => {
  try {
    return JSON.parse(window.localStorage.getItem(REFERRAL_STORAGE_KEY));
  } catch (e) {
    return null;
  }
};

const writeStorage = (value) => {
  try {
    window.localStorage.setItem(REFERRAL_STORAGE_KEY, JSON.stringify(value));
  } catch (e) {
    // storage blocked: the referral is not kept
  }
};

const clearStorage = () => {
  try {
    window.localStorage.removeItem(REFERRAL_STORAGE_KEY);
  } catch (e) {
    // storage blocked
  }
};

export const getStoredReferral = (now = Date.now()) => {
  const stored = readStorage();
  const valid = stored && typeof stored.code === 'string'
    && REFERRAL_CODE_PATTERN.test(stored.code) && typeof stored.firstSeen === 'number';
  if (!valid) {
    if (stored) clearStorage();
    return null;
  }
  if (now - stored.firstSeen > REFERRAL_MAX_AGE_MS) {
    clearStorage();
    return null;
  }
  return stored;
};

const beacon = (path, body) => {
  try {
    if (navigator.sendBeacon) navigator.sendBeacon(`${API_URL}/billing/api/v1/referral/${path}`, JSON.stringify(body));
  } catch (e) {
    // fire-and-forget
  }
};

const sendDetails = (visit) => {
  beacon(`visit/${visit.visitId}/details`, visit.details);
  writeStorage({ ...getStoredReferral(), visitId: visit.visitId });
};

const referrerHost = () => {
  try {
    return document.referrer ? new URL(document.referrer).host : undefined;
  } catch (e) {
    return undefined;
  }
};

/**
 * Stores a new ?ref= (a valid stored one is never replaced) and counts the visit.
 * Details (landing path, referrer host, utm, language) are sent only with marketing consent.
 */
export const captureReferral = (now = Date.now()) => {
  const params = new URLSearchParams(window.location.search);
  const code = (params.get('ref') || '').toUpperCase();
  if (!REFERRAL_CODE_PATTERN.test(code) || getStoredReferral(now)) return;

  writeStorage({ code, firstSeen: now });
  const pick = (value, max) => (value ? value.slice(0, max) : undefined);
  currentVisit = {
    visitId: window.crypto.randomUUID(),
    details: {
      landingPath: window.location.pathname.slice(0, 255),
      referrerHost: pick(referrerHost(), 255),
      utmSource: pick(params.get('utm_source'), 100),
      utmMedium: pick(params.get('utm_medium'), 100),
      utmCampaign: pick(params.get('utm_campaign'), 100),
      language: pick(navigator.language, 35),
    },
  };
  beacon('visit', { code, visitId: currentVisit.visitId });

  const consent = readConsent();
  if (consent && consent.marketing) sendDetails(currentVisit);
};

/**
 * Called by the cookie banner: marketing granted → details of this visit;
 * withdrawn → the visit id is dropped
 */
export const onReferralConsentChange = (consent) => {
  const stored = getStoredReferral();
  if (consent && consent.marketing) {
    if (currentVisit && stored && !stored.visitId) sendDetails(currentVisit);
    return;
  }
  currentVisit = null;
  if (stored && stored.visitId) {
    const { visitId, ...rest } = stored;
    writeStorage(rest);
  }
};

/** Adds the stored ref to a panel link: with the consented visit id, else marked as counted */
export const decorateReferralLink = (anchor) => {
  const stored = getStoredReferral();
  if (!stored || !anchor || !anchor.href) return;

  let url;
  try {
    url = new URL(anchor.href);
  } catch (e) {
    return;
  }
  if (!PANEL_HOSTS.includes(url.host) || url.searchParams.has('ref')) return;

  url.searchParams.set('ref', stored.code);
  if (stored.visitId) {
    url.searchParams.set('vid', stored.visitId);
  } else {
    url.searchParams.set('ref_counted', '1');
  }
  // eslint-disable-next-line no-param-reassign
  anchor.href = url.toString();
};
